import {
  assignmentFolderId,
  assignmentFolders,
  type Classroom,
  gradeOverview,
  type HomeworkAssignment,
  type HomeworkRecord,
  type Student,
  studentNumberLabel,
} from "@classroom/domain";
import { type ReactNode, useRef, useState } from "react";
import GradeCategory from "./GradeCategory";
import { useTranslation } from "./i18n";
import { buildCourseworkReport, courseworkCellText } from "./lib/courseworkReport";
import type { RecordExportScope } from "./lib/localRecordExport";
import { navigationId, useNavigationState } from "./lib/workspaceNavigation";
import RecordExportButton from "./RecordExportButton";
import { ToolPageToolbar } from "./ToolPageHeader";

export default function GradeTable({
  termControl,
  classroom,
  students,
  assignments,
  records,
  initialAssignmentId,
  onClearAssignment,
  termLabel,
  onExport,
}: {
  termControl?: ReactNode;
  classroom: Classroom;
  students: Student[];
  assignments: HomeworkAssignment[];
  records: HomeworkRecord[];
  initialAssignmentId?: string;
  onClearAssignment: () => void;
  termLabel?: string;
  onExport: (scope: RecordExportScope) => void;
}) {
  const { t } = useTranslation("gradeOverview");
  const { t: folderCopy } = useTranslation("assignmentRecords");
  const folders = assignmentFolders(classroom);
  const classTasks = assignments.filter((item) => item.classId === classroom.id);
  const initialAssignment = classTasks.find((item) => item.id === initialAssignmentId);
  const initialFolder = initialAssignment ? assignmentFolderId(initialAssignment, folders) : "";
  // A dashboard shortcut starts a new table instance and takes precedence over
  // the remembered table filter. Ordinary refreshes restore that filter.
  const [folder, setFolder] = useNavigationState(
    `gradeTable:${classroom.id}:folder`,
    initialFolder,
    (value): value is string => navigationId(value) && (!initialAssignment || value === initialFolder),
  );
  const [assignmentId, setAssignmentId] = useNavigationState(
    `gradeTable:${classroom.id}:assignment`,
    initialAssignment?.id || "",
    (value): value is string => navigationId(value) && (!initialAssignment || value === initialAssignment.id),
  );
  const [search, setSearch] = useState("");
  const heading = useRef<HTMLHeadingElement>(null);
  const options = [
    { value: "", label: t("categories.all") },
    ...folders.map((item) => ({ value: item.id, label: item.name })),
  ];
  if (classTasks.some((item) => assignmentFolderId(item, folders) === "uncategorized"))
    options.push({ value: "uncategorized", label: folderCopy("folders.uncategorized") });
  const category = options.some((option) => option.value === folder) ? folder : "";
  const folderTasks = classTasks
    .filter((item) => !category || assignmentFolderId(item, folders) === category)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.title.localeCompare(b.title));
  const selectedTask = folderTasks.find((item) => item.id === assignmentId);
  const tasks = selectedTask ? [selectedTask] : folderTasks;
  const rows = gradeOverview(classroom.id, students, tasks, records).students.filter((row) =>
    `${studentNumberLabel(row.student)} ${row.student.name}`.includes(search.trim()),
  );
  const output = (intent?: "print") =>
    onExport({
      ...buildCourseworkReport(
        classroom,
        rows.map((row) => row.student),
        tasks,
        records,
        termLabel,
      ),
      intent,
      label: t("table.title"),
    });
  return (
    <div className="coursework-table-page">
      <ToolPageToolbar
        scope={
          <>
            {termControl}
            <div>
              {selectedTask && (
                <button
                  type="button"
                  className="coursework-back"
                  onClick={() => {
                    setFolder("");
                    setAssignmentId("");
                    onClearAssignment();
                    requestAnimationFrame(() => heading.current?.focus());
                  }}
                >
                  <span aria-hidden="true">← </span>
                  {t("table.backToAll")}
                </button>
              )}
              <h3 className={selectedTask ? "tool-page-context" : "sr-only"} ref={heading} tabIndex={-1}>
                {selectedTask?.title || t("table.title")}
              </h3>
              <span className="coursework-meta">
                {t("table.scope", { students: rows.length, assignments: tasks.length })}
              </span>
            </div>
          </>
        }
        actions={
          <RecordExportButton
            onClick={() => output()}
            onPrint={() => output("print")}
            disabled={tasks.length === 0 || rows.length === 0}
          />
        }
      />
      <div className="coursework-table-filters">
        <GradeCategory
          label={t("table.categoryFilter")}
          value={category}
          onChange={(value) => {
            setFolder(value);
            setAssignmentId("");
            onClearAssignment();
          }}
          options={options}
        />
        <label className="coursework-search">
          <span className="sr-only">{t("students.searchLabel")}</span>
          <input
            value={search}
            placeholder={t("students.searchPlaceholder")}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>
      {tasks.length === 0 ? (
        <p className="coursework-empty">{t("table.noAssignments")}</p>
      ) : (
        <div
          className="coursework-grid-scroll"
          role="region"
          aria-label={t("table.region")}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard users need to scroll the grade matrix.
          tabIndex={0}
        >
          <table className="coursework-grid" aria-label={t("table.title")}>
            <thead>
              <tr>
                <th scope="col" className="coursework-number">
                  {t("table.number")}
                </th>
                <th scope="col" className="coursework-name">
                  {t("table.name")}
                </th>
                {tasks.map((item) => (
                  <th scope="col" key={item.id}>
                    <span className="coursework-column-title">{item.title}</span>
                    <small>{item.dueDate}</small>
                    {item.maxScore !== undefined && <small>{t("table.maximum", { score: item.maxScore })}</small>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.student.id}>
                  <th scope="row" className="coursework-number">
                    {studentNumberLabel(row.student, true)}
                  </th>
                  <th scope="row" className="coursework-name">
                    {row.student.name}
                  </th>
                  {row.entries.map((entry) => (
                    <td
                      key={entry.assignment.id}
                      className={
                        entry.record?.status === "missing"
                          ? "coursework-grid-missing"
                          : entry.record?.status === "excused" || entry.record?.score === undefined
                            ? "coursework-grid-muted"
                            : ""
                      }
                      title={entry.record?.note}
                    >
                      {courseworkCellText(entry.assignment, entry.record)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length === 0 && <p className="coursework-empty">{t("students.empty")}</p>}
    </div>
  );
}
